import React, { useState, useEffect } from "react";
import {
  Modal,
  TextField,
  Typography,
  TextDescription,
  Select,
} from "@ann/web-components";
import { observer } from "mobx-react";
import { useToast } from "../../../../../../../../../components/toast";
import { ModalBodyContent, ErrorMessage } from "./EditDepartmentModal.styled";

const EditDepartmentModal = ({
  size = "medium",
  isOpen,
  onClose,
  onUpdateDepartment,
  department = null,
  existingNames = [],
  adminOwnerOptions = [],
  loadingUsers = false,
  errorUsers = null,
}) => {
  const toast = useToast();
  const [departmentName, setDepartmentName] = useState("");
  const [description, setDescription] = useState("");
  const [adminOwner, setAdminOwner] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showDuplicateError, setShowDuplicateError] = useState(false);

  useEffect(() => {
    if (!isOpen || !department) return;

    setDepartmentName(department.department_name || department.name || "");
    setDescription(department.description || "");

    const ownerUuid = department.owner_uuid || department.ownerUuid || "";
    const ownerName = department.owner_name || department.ownerName || "";
    const ownerEmail = department.owner_email || department.ownerEmail || "";

    if (!ownerUuid && !ownerName && !ownerEmail) {
      setAdminOwner(null);
      return;
    }

    const existingOwner = adminOwnerOptions.find(
      (option) => option.value === ownerUuid
    );

    setAdminOwner(
      existingOwner || (ownerName || ownerEmail
        ? {
            label: `${ownerName || ownerEmail} - Department Admin`,
            value: ownerUuid || ownerName?.toLowerCase?.()?.replace(/\s+/g, "_") || ownerEmail,
          }
        : null)
    );
  }, [isOpen, department, adminOwnerOptions]);

  const originalName = department?.department_name || department?.name || "";
  const trimmedName = departmentName.trim();
  const isDuplicateName =
    trimmedName.length > 0 &&
    trimmedName.toLowerCase() !== originalName.toLowerCase() &&
    existingNames.some(
      (name) => name.toLowerCase().trim() === trimmedName.toLowerCase()
    );
  const isFormValid = trimmedName.length > 0;

  const handleSave = async () => {
    if (isDuplicateName) {
      setShowDuplicateError(true);
      return;
    }
    if (!isFormValid) return;

    try {
      setIsUpdating(true);
      const departmentData = {
        name: departmentName,
        description,
        adminOwner,
        adminOwnerName: adminOwner?.label?.split(" - ")[0] || "",
      };
      await onUpdateDepartment?.(departmentData);
    } catch (error) {
      console.error("Error updating department:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleClose = () => {
    setDepartmentName("");
    setDescription("");
    setAdminOwner(null);
    setIsUpdating(false);
    setShowDuplicateError(false);
    onClose();
  };

  const handleNameChange = (value) => {
    setDepartmentName(value);
    if (showDuplicateError) setShowDuplicateError(false);
  };

  const handleAdminOwnerChange = (value) => {
    const selectedOption = adminOwnerOptions.find(
      (option) => option.value === value
    );
    setAdminOwner(selectedOption || null);
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} width="900px" size={size}>
      <Modal.Header>
        <Modal.Header.Title>Edit Department</Modal.Header.Title>
        <Modal.Header.Description>
          <Typography
            category="body"
            size="medium"
            weight="regular"
            color="body"
          >
            Update department information and configuration.
          </Typography>
        </Modal.Header.Description>
      </Modal.Header>
      <Modal.Body>
        <ModalBodyContent>
          <div>
            <TextField
              label="Department Name"
              size="small"
              placeholder="e.g., Sales & Business Development"
              value={departmentName}
              onChange={handleNameChange}
              required={true}
              error={showDuplicateError && isDuplicateName}
            />
            {showDuplicateError && isDuplicateName && (
              <ErrorMessage>
                A department with this name already exists
              </ErrorMessage>
            )}
          </div>

          <TextDescription
            height="148px"
            label="Description"
            value={description}
            onChange={(value) => setDescription(value)}
            placeholder="Describe the department's purpose and scope..."
            size="small"
          />

          <Select
            label="Admin Owner"
            value={adminOwner || null}
            onChange={handleAdminOwnerChange}
            placeholder={loadingUsers ? "Loading users..." : "Select admin owner"}
            size="small"
            width="100%"
            disabled={loadingUsers}
          >
            <Select.Menu>
              {adminOwnerOptions.length > 0 ? (
                adminOwnerOptions.map((option) => (
                  <Select.Item
                    key={option.value}
                    label={option.label}
                    value={option.value}
                  >
                    {option.label}
                  </Select.Item>
                ))
              ) : (
                <Select.Item label="No users available" value="" disabled>
                  No users available
                </Select.Item>
              )}
            </Select.Menu>
          </Select>
          {errorUsers && (
            <ErrorMessage>
              We couldn&apos;t load users. Please try again.
            </ErrorMessage>
          )}
        </ModalBodyContent>
      </Modal.Body>
      <Modal.Actions align="end">
        <Modal.Button
          label="Cancel"
          onClick={handleClose}
          type="secondary"
          disabled={isUpdating}
        />
        <Modal.Button
          label={isUpdating ? "Saving..." : "Save Changes"}
          onClick={handleSave}
          disabled={!isFormValid || isUpdating}
        />
      </Modal.Actions>
    </Modal>
  );
};

export default observer(EditDepartmentModal);
